import React, { useEffect, useRef, useState } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";

// ============================================================
// API URL
// ============================================================

const BASE_URL = process.env.REACT_APP_API_URL;

const API_URL = `${BASE_URL}/api/currency-exchange-rates`;

// ============================================================
// Currency Master
// ============================================================

const currencyOptions = [
  {
    label: "USD - US Dollar",
    value: "USD",
  },
  {
    label: "EUR - Euro",
    value: "EUR",
  },
  {
    label: "GBP - British Pound",
    value: "GBP",
  },
  {
    label: "INR - Indian Rupee",
    value: "INR",
  },
  {
    label: "AED - UAE Dirham",
    value: "AED",
  },
  {
    label: "SAR - Saudi Riyal",
    value: "SAR",
  },
  {
    label: "QAR - Qatari Riyal",
    value: "QAR",
  },
  {
    label: "OMR - Omani Rial",
    value: "OMR",
  },
  {
    label: "KWD - Kuwaiti Dinar",
    value: "KWD",
  },
  {
    label: "BHD - Bahraini Dinar",
    value: "BHD",
  },
  {
    label: "CAD - Canadian Dollar",
    value: "CAD",
  },
  {
    label: "AUD - Australian Dollar",
    value: "AUD",
  },
  {
    label: "SGD - Singapore Dollar",
    value: "SGD",
  },
  {
    label: "HKD - Hong Kong Dollar",
    value: "HKD",
  },
  {
    label: "JPY - Japanese Yen",
    value: "JPY",
  },
  {
    label: "CNY - Chinese Yuan",
    value: "CNY",
  },
  {
    label: "CHF - Swiss Franc",
    value: "CHF",
  },
  {
    label: "NZD - New Zealand Dollar",
    value: "NZD",
  },
  {
    label: "MYR - Malaysian Ringgit",
    value: "MYR",
  },
  {
    label: "THB - Thai Baht",
    value: "THB",
  },
  {
    label: "ZAR - South African Rand",
    value: "ZAR",
  },
  {
    label: "KRW - South Korean Won",
    value: "KRW",
  },
  {
    label: "SEK - Swedish Krona",
    value: "SEK",
  },
  {
    label: "NOK - Norwegian Krone",
    value: "NOK",
  },
  {
    label: "DKK - Danish Krone",
    value: "DKK",
  },
  {
    label: "BRL - Brazilian Real",
    value: "BRL",
  },
  {
    label: "MXN - Mexican Peso",
    value: "MXN",
  },
  {
    label: "TRY - Turkish Lira",
    value: "TRY",
  },
  {
    label: "PLN - Polish Zloty",
    value: "PLN",
  },
  {
    label: "RUB - Russian Ruble",
    value: "RUB",
  },
  {
    label: "IDR - Indonesian Rupiah",
    value: "IDR",
  },
  {
    label: "VND - Vietnamese Dong",
    value: "VND",
  },
  {
    label: "PHP - Philippine Peso",
    value: "PHP",
  },
  {
    label: "PKR - Pakistani Rupee",
    value: "PKR",
  },
  {
    label: "BDT - Bangladeshi Taka",
    value: "BDT",
  },
  {
    label: "LKR - Sri Lankan Rupee",
    value: "LKR",
  },
];

// ============================================================
// Empty Record
// ============================================================

const createEmptyRecord = () => ({
  id: null,
  currency: null,
  exchange_rate: null,
  valid_from: null,
  valid_to: null,
  isNew: true,
});

// ============================================================
// Component
// ============================================================

const CurrencyExchangeRateConfiguration = () => {
  const toast = useRef(null);

  const [records, setRecords] = useState([]);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);

  // ==========================================================
  // Toast helper
  // ==========================================================

  const showToast = (severity, summary, detail) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3500,
    });
  };

  // ==========================================================
  // Fetch Records
  // ==========================================================

  const fetchRecords = async () => {
    try {
      setLoading(true);

      const response = await fetch("/apis/currency-exchange-rates", {
        method: "GET",
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Failed to fetch currency exchange rates.",
        );
      }

      const formattedRecords = Array.isArray(result)
        ? result.map((item) => ({
            ...item,

            exchange_rate:
              item.exchange_rate !== null && item.exchange_rate !== undefined
                ? Number(item.exchange_rate)
                : null,

            valid_from: item.valid_from ? new Date(item.valid_from) : null,

            valid_to: item.valid_to ? new Date(item.valid_to) : null,

            isNew: false,
          }))
        : [];

      setRecords(formattedRecords);
    } catch (error) {
      console.error("Error fetching exchange rates:", error);

      showToast(
        "error",
        "Error",
        error.message || "Failed to fetch exchange rates.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // Initial Load
  // ==========================================================

  useEffect(() => {
    fetchRecords();
  }, []);

  // ==========================================================
  // Add New Row
  // ==========================================================

  const handleAdd = () => {
    // Prevent multiple new rows
    const hasNewRecord = records.some((record) => record.isNew);

    if (hasNewRecord) {
      showToast(
        "warn",
        "Pending Record",
        "Please save or cancel the current record first.",
      );

      return;
    }

    // Prevent adding while another record is being edited
    if (editingId !== null) {
      showToast(
        "warn",
        "Edit in Progress",
        "Please complete the current edit first.",
      );

      return;
    }

    const newRecord = createEmptyRecord();

    setRecords((prev) => [newRecord, ...prev]);

    setEditingId("new");
  };

  // ==========================================================
  // Update Local Row
  // ==========================================================

  const handleFieldChange = (rowData, field, value) => {
    const rowId = rowData.isNew ? "new" : rowData.id;

    setRecords((prev) =>
      prev.map((record) => {
        const currentId = record.isNew ? "new" : record.id;

        if (currentId !== rowId) {
          return record;
        }

        return {
          ...record,
          [field]: value,
        };
      }),
    );
  };

  // ==========================================================
  // Validate Record
  // ==========================================================

  const validateRecord = (record) => {
    if (!record.currency) {
      showToast("warn", "Validation", "Please select a currency.");

      return false;
    }

    if (
      record.exchange_rate === null ||
      record.exchange_rate === undefined ||
      record.exchange_rate === ""
    ) {
      showToast("warn", "Validation", "Please enter the exchange rate.");

      return false;
    }

    const exchangeRate = Number(record.exchange_rate);

    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
      showToast("warn", "Validation", "Exchange rate must be greater than 0.");

      return false;
    }

    if (!record.valid_from) {
      showToast("warn", "Validation", "Please select the Valid From date.");

      return false;
    }

    if (!record.valid_to) {
      showToast("warn", "Validation", "Please select the Valid To date.");

      return false;
    }

    const fromDate = new Date(record.valid_from);

    const toDate = new Date(record.valid_to);

    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      showToast("warn", "Validation", "Please enter valid dates.");

      return false;
    }

    if (fromDate > toDate) {
      showToast(
        "warn",
        "Validation",
        "Valid From date cannot be greater than Valid To date.",
      );

      return false;
    }

    return true;
  };

  // ==========================================================
  // Convert Record for API
  // ==========================================================

  const getRequestBody = (record) => ({
    currency: record.currency?.trim().toUpperCase(),

    exchange_rate: Number(record.exchange_rate),

    valid_from: record.valid_from ? record.valid_from.toISOString() : null,

    valid_to: record.valid_to ? record.valid_to.toISOString() : null,
  });

  // ==========================================================
  // Save New Record
  // ==========================================================

  const handleSave = async (record) => {
    if (!validateRecord(record)) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch("/apis/currency-exchange-rates", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(getRequestBody(record)),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to save exchange rate.");
      }

      showToast(
        "success",
        "Saved",
        "Currency exchange rate saved successfully.",
      );

      setEditingId(null);

      await fetchRecords();
    } catch (error) {
      console.error("Error saving exchange rate:", error);

      showToast(
        "error",
        "Save Failed",
        error.message || "Failed to save exchange rate.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // Update Existing Record
  // ==========================================================

  const handleUpdate = async (record) => {
    if (!validateRecord(record)) {
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        `/apis/currency-exchange-rates/${record.id}`,
        {
          method: "PUT",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(getRequestBody(record)),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to update exchange rate.");
      }

      showToast(
        "success",
        "Updated",
        "Currency exchange rate updated successfully.",
      );

      setEditingId(null);

      await fetchRecords();
    } catch (error) {
      console.error("Error updating exchange rate:", error);

      showToast(
        "error",
        "Update Failed",
        error.message || "Failed to update exchange rate.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // Cancel
  // ==========================================================

  const handleCancel = (record) => {
    // If new record, remove row
    if (record.isNew) {
      setRecords((prev) => prev.filter((item) => !item.isNew));

      setEditingId(null);

      return;
    }

    // Existing record
    setEditingId(null);

    // Reload original values from server
    fetchRecords();
  };

  // ==========================================================
  // Delete
  // ==========================================================

  const handleDelete = (record) => {
    confirmDialog({
      header: "Delete Exchange Rate",

      message:
        `Are you sure you want to delete the ` +
        `${record.currency} exchange rate configuration?`,

      icon: "pi pi-exclamation-triangle",

      acceptClassName: "p-button-danger",

      acceptLabel: "Delete",

      rejectLabel: "Cancel",

      accept: async () => {
        try {
          setSaving(true);

          const response = await fetch(
            `/apis/currency-exchange-rates/${record.id}`,
            {
              method: "DELETE",
            },
          );

          const result = await response.json();

          if (!response.ok) {
            throw new Error(
              result?.message || "Failed to delete exchange rate.",
            );
          }

          showToast(
            "success",
            "Deleted",
            "Currency exchange rate deleted successfully.",
          );

          await fetchRecords();
        } catch (error) {
          console.error("Error deleting exchange rate:", error);

          showToast(
            "error",
            "Delete Failed",
            error.message || "Failed to delete exchange rate.",
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // ============================================================
  // Status Column
  // ============================================================

  const statusBodyTemplate = (rowData) => {
    // New/editing record without dates
    if (!rowData.valid_from || !rowData.valid_to) {
      return <Tag value="Inactive" severity="danger" />;
    }

    const currentDate = new Date();
    const validFrom = new Date(rowData.valid_from);
    const validTo = new Date(rowData.valid_to);

    const isActive = currentDate >= validFrom && currentDate <= validTo;

    return (
      <Tag
        value={isActive ? "Active" : "Inactive"}
        severity={isActive ? "success" : "danger"}
        icon={isActive ? "pi pi-check-circle" : "pi pi-times-circle"}
      />
    );
  };

  // ==========================================================
  // Currency Column
  // ==========================================================

  const currencyBodyTemplate = (rowData) => {
    const isEditing = rowData.isNew || editingId === rowData.id;

    if (!isEditing) {
      const currency = currencyOptions.find(
        (item) => item.value === rowData.currency,
      );

      return (
        <div className="flex align-items-center gap-2">
          <Tag value={rowData.currency || "-"} severity="info" />

          <span className="text-700">
            {currency?.label?.replace(`${rowData.currency} - `, "") || ""}
          </span>
        </div>
      );
    }

    return (
      <Dropdown
        value={rowData.currency}
        options={currencyOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select Currency"
        filter
        showClear
        className="w-full"
        onChange={(e) => handleFieldChange(rowData, "currency", e.value)}
      />
    );
  };

  // ==========================================================
  // Exchange Rate Column
  // ==========================================================

  const exchangeRateBodyTemplate = (rowData) => {
    const isEditing = rowData.isNew || editingId === rowData.id;

    if (!isEditing) {
      return (
        <span className="font-semibold text-700">
          {Number(rowData.exchange_rate).toFixed(6)}
        </span>
      );
    }

    return (
      <InputNumber
        value={rowData.exchange_rate}
        onValueChange={(e) =>
          handleFieldChange(rowData, "exchange_rate", e.value)
        }
        mode="decimal"
        min={0}
        minFractionDigits={2}
        maxFractionDigits={6}
        useGrouping={false}
        placeholder="Enter exchange rate"
        className="w-full"
        inputClassName="w-full"
      />
    );
  };

  // ==========================================================
  // Valid From Column
  // ==========================================================

  const validFromBodyTemplate = (rowData) => {
    const isEditing = rowData.isNew || editingId === rowData.id;

    if (!isEditing) {
      return rowData.valid_from
        ? rowData.valid_from.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-";
    }

    return (
      <Calendar
        value={rowData.valid_from}
        onChange={(e) => handleFieldChange(rowData, "valid_from", e.value)}
        dateFormat="dd/mm/yy"
        placeholder="Valid From"
        showIcon
        className="w-full"
        maxDate={rowData.valid_to || undefined}
        readOnlyInput
      />
    );
  };

  // ==========================================================
  // Valid To Column
  // ==========================================================

  const validToBodyTemplate = (rowData) => {
    const isEditing = rowData.isNew || editingId === rowData.id;

    if (!isEditing) {
      return rowData.valid_to
        ? rowData.valid_to.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-";
    }

    return (
      <Calendar
        value={rowData.valid_to}
        onChange={(e) => handleFieldChange(rowData, "valid_to", e.value)}
        dateFormat="dd/mm/yy"
        placeholder="Valid To"
        showIcon
        className="w-full"
        minDate={rowData.valid_from || undefined}
        readOnlyInput
      />
    );
  };

  // ==========================================================
  // Action Column
  // ==========================================================

  const actionBodyTemplate = (rowData) => {
    const isEditing = rowData.isNew || editingId === rowData.id;

    if (isEditing) {
      return (
        <div className="flex align-items-center gap-1">
          {/* Save */}
          <Button
            icon="pi pi-check"
            rounded
            text
            severity="success"
            tooltip="Save"
            tooltipOptions={{
              position: "top",
            }}
            disabled={saving}
            onClick={() =>
              rowData.isNew ? handleSave(rowData) : handleUpdate(rowData)
            }
          />

          {/* Cancel */}
          <Button
            icon="pi pi-times"
            rounded
            text
            severity="secondary"
            tooltip="Cancel"
            tooltipOptions={{
              position: "top",
            }}
            disabled={saving}
            onClick={() => handleCancel(rowData)}
          />
        </div>
      );
    }

    return (
      <div className="flex align-items-center gap-1">
        {/* Edit */}
        <Button
          icon="pi pi-pencil"
          rounded
          text
          severity="info"
          tooltip="Edit"
          tooltipOptions={{
            position: "top",
          }}
          disabled={editingId !== null || saving}
          onClick={() => setEditingId(rowData.id)}
        />

        {/* Delete */}
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          tooltip="Delete"
          tooltipOptions={{
            position: "top",
          }}
          disabled={editingId !== null || saving}
          onClick={() => handleDelete(rowData)}
        />
      </div>
    );
  };

  // ==========================================================
  // Header
  // ==========================================================

  const tableHeader = (
    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
      <div>
        <div className="flex align-items-center gap-2">
          <i className="pi pi-dollar text-primary text-xl" />

          <h4 className="m-0">Currency Exchange Rate</h4>
        </div>

        <div className="text-600 text-sm mt-1">
          Configure exchange rates and their validity periods.
        </div>
      </div>

      <Button
        label="Add"
        icon="pi pi-plus"
        severity="primary"
        onClick={handleAdd}
        disabled={
          loading ||
          saving ||
          editingId !== null ||
          records.some((record) => record.isNew)
        }
      />
    </div>
  );

  // ==========================================================
  // Empty Table
  // ==========================================================

  const emptyMessage = (
    <div className="flex flex-column align-items-center justify-content-center py-5">
      <i
        className="pi pi-dollar text-400"
        style={{
          fontSize: "2.5rem",
        }}
      />

      <div className="text-700 font-semibold mt-3">
        No Exchange Rates Configured
      </div>

      <div className="text-500 text-sm mt-1">
        Click the Add button to create your first exchange rate.
      </div>
    </div>
  );

  // ==========================================================
  // Render
  // ==========================================================

  return (
    <div className="p-3">
      {/* Toast */}
      <Toast ref={toast} />

      {/* Confirm Dialog */}
      <ConfirmDialog />

      {/* Page Header */}
      <div className="mb-3">
        <div className="flex align-items-center gap-2">
          <i
            className="pi pi-cog text-primary"
            style={{
              fontSize: "1.4rem",
            }}
          />

          <h2 className="m-0 text-900">Configurations</h2>
        </div>

        <div className="text-600 text-sm mt-1">
          Manage system master data and configuration settings.
        </div>
      </div>

      {/* Main Card */}
      <div className="card p-0">
        <DataTable
          value={records}
          dataKey="id"
          loading={loading}
          header={tableHeader}
          emptyMessage={emptyMessage}
          stripedRows
          responsiveLayout="scroll"
          className="p-datatable-sm"
          rowHover
          showGridlines={false}
        >
          {/* Currency */}
          <Column
            header="Currency"
            body={currencyBodyTemplate}
            style={{
              minWidth: "250px",
            }}
          />

          {/* Exchange Rate */}
          <Column
            header="Exchange Rate"
            body={exchangeRateBodyTemplate}
            style={{
              minWidth: "190px",
            }}
          />

          {/* Valid From */}
          <Column
            header="Valid From"
            body={validFromBodyTemplate}
            style={{
              minWidth: "190px",
            }}
          />

          {/* Valid To */}
          <Column
            header="Valid To"
            body={validToBodyTemplate}
            style={{
              minWidth: "190px",
            }}
          />

          <Column
            header="Status"
            body={statusBodyTemplate}
            style={{
              minWidth: "130px",
            }}
          />

          {/* Actions */}
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{
              width: "150px",
            }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default CurrencyExchangeRateConfiguration;
